import { useAtom, useAtomValue } from "jotai";
import { Save, ShoppingCart } from "lucide-react";

import { refineArtworkOpenAtom } from "@design-lab/entities/artwork";
import { pinningModeAtom } from "@design-lab/entities/pinning";
import { getUserType } from "@design-lab/entities/user";
import {
  useAddToCartModal,
  useEditCart,
  useReviewPinningModal
} from "@design-lab/features/manage-cart";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { ShopifyRecipeButton } from "@design-lab/features/shopify-recipe";
import { cn } from "@design-lab/shared";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { trackDesignLabCtaClicked } from "@design-lab/shared/lib/analytics";
import { useIsSample, useIsShopifyRecipeCreator } from "@design-lab/shared/lib/config";
import {
  addToCartLoadingAtom,
  saveDesignLoadingAtom,
  shopifyRecipeLoadingAtom
} from "@design-lab/shared/state";
import { Button } from "@design-lab/shared/ui";
import { useNotification } from "@design-lab/shared/ui/notification";
import { redirectTo } from "common/utils/browser";
import { useCompositionValidation } from "../model/useCompositionValidation";

/**
 * Renders the Add to Cart toolbar with actions for adding the design to cart and optionally saving the project.
 *
 * The toolbar shows a primary Add to Cart button and, when not in edit mode or a sample, a Save Project button.
 * Buttons are disabled or show loading states based on validation, edit/save/add-to-cart loading state, and refine mode.
 * Submitting validates compositions, handles edit or add-to-cart flows, intercepts desktop green-screen verification, and surfaces sample-related errors via notifications.
 *
 * @returns A React element containing the Add to Cart button and the optional Save Project button, with wiring for validation, loading state, analytics tracking, green-screen interception, and sample-related error notifications.
 */
export function AddToCartToolbar() {
  const { isEditMode } = useEditMode();
  const { handleAddToCartClick, isAddingToCart } = useAddToCartModal();
  const { handleEditCart, isEditingCart } = useEditCart();
  const { isDesktop } = useResponsive();
  const validation = useValidation();
  const { validateCompositions } = useCompositionValidation();
  const isSample = useIsSample();
  const userType = getUserType();
  const { notify } = useNotification();
  const isSaveDesignLoading = useAtomValue(saveDesignLoadingAtom);
  const [isAddToCartLoading, setIsAddToCartLoading] = useAtom(addToCartLoadingAtom);
  const [isShopifyRecipeLoading] = useAtom(shopifyRecipeLoadingAtom);
  const { setIsSaveProjectVisible } = useSaveProject();

  const [isRefineMode, setIsRefineMode] = useAtom(refineArtworkOpenAtom);
  const isPinningMode = useAtomValue(pinningModeAtom);
  const { hasCompletedSessions, openModal: openReviewModal } = useReviewPinningModal();
  const isShopifyRecipeCreator = useIsShopifyRecipeCreator();

  const isSubmitting = isEditMode ? isEditingCart : isAddingToCart || isAddToCartLoading;
  const buttonText = isEditMode ? "SAVE CHANGES" : "ADD TO CART";
  const loadingText = isEditMode ? "SAVING..." : "ADDING...";

  const isAnyLoading = isSubmitting || isSaveDesignLoading || isShopifyRecipeLoading;

  const isDisabled =
    isAnyLoading ||
    isPinningMode ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading ||
    !validation.hasProduct ||
    !validation.isQuantityValid;

  const isSaveDesignDisabled =
    isAnyLoading || !validation.isArtworkUploaded || validation.isSomeArtworkLoading;

  const proceedWithAddToCart = async () => {
    trackDesignLabCtaClicked("add_to_cart");

    setIsAddToCartLoading(true);
    try {
      await handleAddToCartClick(false, () => redirectTo("/cart/buy"));
      // Don't reset to false on success - page is navigating away and we need
      // the atom to remain true to prevent auto-save error handlers from redirecting
    } catch (error: any) {
      setIsAddToCartLoading(false);

      // Handle CombinedGraphQLErrors which is thrown by Apollo Client with errorPolicy: 'none'
      if (error?.name === "CombinedGraphQLErrors" || error?.name === "ApolloError") {
        // The error message contains our sample validation messages
        if (error?.message?.includes("sample") || error?.message?.includes("Sample")) {
          notify({ title: "Sample Limit Reached", description: error.message, variant: "error" });
          return;
        }
      }

      // Also check for graphQLErrors property (different error structures)
      const graphQLErrors = error?.graphQLErrors || [];
      if (graphQLErrors.length > 0) {
        const firstError = graphQLErrors[0];
        const errorCode = firstError?.extensions?.code;

        if (
          errorCode === "SAMPLE_ALREADY_IN_CART" ||
          errorCode === "SAMPLE_ORDER_LIMIT_REACHED" ||
          errorCode === "SAMPLE_LIMIT_ONE_PER_CART"
        ) {
          notify({
            title: "Sample Limit Reached",
            description: firstError.message,
            variant: "error"
          });
          return;
        }
      }

      // Re-throw other errors to maintain existing error handling
      throw error;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateCompositions();
    if (!isValid) return;

    if (isRefineMode) {
      setIsRefineMode(false);
    }

    const proceedAction = isEditMode ? handleEditCart : proceedWithAddToCart;

    // Show review modal if there are completed pinning sessions
    if (hasCompletedSessions) {
      openReviewModal(proceedAction);
      return;
    }

    await proceedAction();
  };

  const handleSaveAndStartNew = async () => {
    trackDesignLabCtaClicked("save_and_start_new_design");
    setIsSaveProjectVisible(true);
  };

  return (
    <div className="flex w-full flex-col-reverse items-center gap-2 lg:w-auto lg:flex-row lg:gap-4">
      {!isEditMode && !isSample && (
        <div className="flex w-full gap-2">
          <Button
            data-testid="save-start-new-button"
            onPress={userType === "guest_user" ? () => {} : handleSaveAndStartNew}
            variant="outline"
            size={isDesktop ? "3xl" : "xl"}
            className={cn(
              "order-2 w-full border border-blue-700 lg:order-none lg:h-12 lg:w-auto lg:min-w-48 xl:h-16",
              userType === "guest_user" && "js-sign-in-modal-toggle"
            )}
            isDisabled={isSaveDesignDisabled}
          >
            <Save className="size-5 lg:size-6" />
            Save Project
          </Button>
          {isShopifyRecipeCreator && (
            <ShopifyRecipeButton isAnyLoading={isAnyLoading} isDesktop={isDesktop} />
          )}
        </div>
      )}
      {!isShopifyRecipeCreator && (
        <Button
          data-testid="add-to-cart-button"
          onPress={handleSubmit}
          variant="destructive"
          size={isDesktop ? "3xl" : "xl"}
          isDisabled={isDisabled}
          className="order-1 w-full lg:order-none lg:h-12 lg:w-auto lg:min-w-48 xl:h-16"
        >
          <ShoppingCart className="size-5 lg:size-6" />
          {isSubmitting ? loadingText : buttonText}
        </Button>
      )}
    </div>
  );
}
